import { Component, EventEmitter, Output, ChangeDetectorRef, ViewChild, OnDestroy, OnInit, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { EfileAdminService } from '../efile-admin.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-efile-additional-elements',
    templateUrl: './efile-additional-elements.component.html',
    styleUrls: ['./efile-additional-elements.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EfileAdditionalElementsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  title = 'Add/Edit Additional Elements';
  minimise = false;
  saving = false;

  @ViewChild('dataServiceForm') dataServiceForm!: NgForm;

  constructor(
    private efileAdminService: EfileAdminService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    const row = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(activeScreen));
    this.rowData = { ...row };
    this.cdr.markForCheck();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  save(): void {
    if (this.saving) return;
    if (this.dataServiceForm?.invalid) {
      this.gtwSnackbarService.openSnackBar('Please fill all required fields.', 'gtw-snackbar--red');
      return;
    }
    this.saving = true;
    this.cdr.detectChanges();

    const checkLockedbj = [{
      leid: this.rowData?.LEID ?? this.rowData?.SOURCE_LEID ?? '',
      me_code: this.rowData?.ME_CODE ?? this.rowData?.ME ?? this.rowData?.SOURCE_ME_CODE ?? '',
      cdr_number: this.rowData?.CDR_NO ?? this.rowData?.TAX_CODE ?? this.rowData?.SOURCE_CDR_NO ?? '',
      consol_group_key: this.rowData?.CONSOL_GROUP_KEY ?? this.rowData?.FILING_GROUP ?? '',
      reporting_period: this.rowData?.REPORTING_PERIOD ?? this.rowData?.SOURCE_REPORTING_PERIOD ?? '',
      combination_key: this.rowData?.COMBINATION_KEY ?? this.rowData?.COMPL_COMBINATION_KEY ?? this.rowData?.HO_COMBINATION_KEY ?? '',
      scenario_key: this.rowData?.SCENARIO_KEY ?? this.filterParams?.scenario ?? ''
    }];
    const params = {
      ...this.rowData,
      checkLockedbj: JSON.stringify(checkLockedbj)
    };

    this.efileAdminService.saveJsonObject(
      this.GENERAL_CONFIG?.base_url ?? '',
      32868,
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.saving = false;
        this.cdr.detectChanges();
        if (data?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Data saved successfully.', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          const errorMessage = (data?.errorMessage && data.errorMessage !== 'null') ? data.errorMessage : 'Error saving.';
          this.gtwSnackbarService.openSnackBar(errorMessage, 'gtw-snackbar--red');
        }
      },
      error: () => {
        this.saving = false;
        this.cdr.detectChanges();
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
      }
    });
  }
}
